/*
 * «يُشترى معه عادةً» as a bundle.
 *
 * The theme's own complementary block gives every recommendation its own "+"
 * button, so the shopper adds them one at a time. This turns the block into a
 * pick list instead: the viewed product is the first row, everything in stock
 * starts ticked, the total follows the ticks, and one button adds the whole
 * selection in a single /cart/add call.
 *
 * The theme's `add_as_bundle` block setting is a different feature — it adds
 * the main product alongside a recommendation — and is left alone.
 */
if (!window.Eurus.loadedScript.has('x-complementary-bundle.js')) {
  window.Eurus.loadedScript.add('x-complementary-bundle.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xComplementaryBundle', (items, moneyFormat, sectionId) => ({
        items: items,
        moneyFormat: moneyFormat,
        sectionId: sectionId,
        picked: {},
        chosenVariant: {},
        loading: false,

        init() {
          // Sold-out rows start unticked: they cannot be bought and must not
          // inflate the total.
          this.items.forEach((item) => {
            this.chosenVariant[item.key] = String(item.variant);
            this.picked[item.key] = item.available;
          });

          // The first row is the product being viewed, and its variant picker
          // lives outside this component — follow it so the row, the total and
          // what goes in the cart are the variant actually on screen.
          const main = this.items.find((item) => item.main);
          if (main) {
            this.$root._xBundleVariantSync = (event) => {
              const variant = event.detail && event.detail.variant;
              if (!variant) return;
              this.chosenVariant[main.key] = String(variant.id);
              this.syncVariant(main.key);
            };
            document.addEventListener('eurus:variant:change', this.$root._xBundleVariantSync);
            // The picker may already have moved before this block loaded.
            const live = this.liveMainVariant();
            if (live) {
              this.chosenVariant[main.key] = live;
              this.syncVariant(main.key);
            }
          }
        },

        destroy() {
          if (this.$root._xBundleVariantSync) {
            document.removeEventListener('eurus:variant:change', this.$root._xBundleVariantSync);
          }
        },

        // The theme writes the chosen variant into the main product form.
        liveMainVariant() {
          const input = document.getElementById(`update-variant-${this.sectionId}`);
          return input && input.value ? String(input.value) : null;
        },

        variantOf(key) {
          const item = this.items.find((i) => i.key === key);
          if (!item) return null;
          const chosen = String(this.chosenVariant[key]);
          return item.variants.find((v) => String(v.id) === chosen) || null;
        },

        sellable(key) {
          const variant = this.variantOf(key);
          return !!variant && variant.available;
        },

        toggle(key) {
          if (!this.sellable(key)) return;
          this.picked[key] = !this.picked[key];
        },

        // A variant switch on a multi-variant product can land on a sold-out
        // size; drop it from the selection rather than fail at /cart/add.
        syncVariant(key) {
          if (!this.sellable(key)) this.picked[key] = false;
        },

        get chosen() {
          return this.items.filter((item) => this.picked[item.key] && this.sellable(item.key));
        },

        get total() {
          return this.chosen.reduce((sum, item) => {
            const variant = this.variantOf(item.key);
            return sum + (variant ? variant.price : 0);
          }, 0);
        },

        get totalText() {
          return Alpine.store('xHelper').formatMoney(this.total, this.moneyFormat);
        },

        price(key) {
          const variant = this.variantOf(key);
          return variant ? Alpine.store('xHelper').formatMoney(variant.price, this.moneyFormat) : '';
        },

        // The form is the source of truth for the viewed product at the moment
        // of adding, even if no variant event ever reached this component.
        variantIdToAdd(item) {
          if (item.main) {
            const live = this.liveMainVariant();
            if (live && item.variants.some((v) => String(v.id) === live)) return live;
          }
          return this.variantOf(item.key).id;
        },

        async addAll() {
          const chosen = this.chosen;
          if (this.loading || chosen.length === 0) return;
          this.loading = true;

          try {
            const response = await fetch(`${Eurus.cart_add_url}.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                items: chosen.map((item) => ({ id: this.variantIdToAdd(item), quantity: 1 })),
                // Asking for the cart sections in the same request is what the
                // theme's own add-to-cart does: re-rendering them from a second
                // fetch races the cart cookie and opens an empty-looking drawer.
                sections: Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname
              })
            }).then((res) => res.json());

            if (response.status == '422' || response.status === 422) {
              document.dispatchEvent(new CustomEvent('eurus:product:add-failed', {
                detail: { errorMessage: response.description }
              }));
              return;
            }

            // Same after-add sequence the theme's own add-to-cart runs, so the
            // bubble, the drawer and the cart notification all stay in step.
            if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
              Alpine.store('xCartNoti').setItem(response);
            }
            await Alpine.store('xCartHelper').reRenderSections(response.sections);
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML);
            }
            if (Alpine.store('xMiniCart') && (!Alpine.store('xCartNoti') || !Alpine.store('xCartNoti').enable)) {
              Alpine.store('xMiniCart').openCart();
            }
            document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
            document.dispatchEvent(new CustomEvent('eurus:product:added', {
              detail: { product: response }
            }));
          } catch (error) {
            document.dispatchEvent(new CustomEvent('eurus:product:add-failed', {
              detail: { errorMessage: error }
            }));
            console.error('Error:', error);
          } finally {
            this.loading = false;
          }
        }
      }));
    });
  });
}
